import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useContext, useRef } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import {
	Copy,
	Loader2,
	Cake,
	ShieldCheck,
	Palette,
	SlidersHorizontal,
	KeyRound,
	Plug,
	Upload,
	Check,
	AlertTriangle,
	ChevronDown,
	ChevronRight,
	DatabaseBackup,
	Share2,
} from "lucide-react";

import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import BackupRestore from "../../components/BackupRestore";
import MetaSettings from "../../components/MetaSettings";
import { AuthContext } from "../../context/Auth/AuthContext";

// Cada aba abre com uma linha dizendo para que ela serve — quem chega aqui
// não deveria ter de adivinhar pelo nome da aba.
const SECTIONS = [
	{
		id: "geral",
		label: "Geral",
		icon: SlidersHorizontal,
		descricao:
			"Quem pode entrar no sistema e como as pessoas criam a conta delas.",
		superAdminOnly: true,
	},
	{
		id: "aparencia",
		label: "Aparência",
		icon: Palette,
		descricao:
			"O nome, a cor e as imagens que sua equipe e seus clientes veem nas telas.",
		superAdminOnly: true,
	},
	{
		id: "automacoes",
		label: "Automações",
		icon: Cake,
		descricao:
			"Mensagens que o sistema envia sozinho, sem ninguém precisar digitar.",
	},
	{
		id: "seguranca",
		label: "Segurança",
		icon: ShieldCheck,
		descricao:
			"Proteção extra para a sua conta na hora de entrar no sistema.",
	},
	{
		id: "api",
		label: "API",
		icon: KeyRound,
		descricao:
			"A senha que outro programa usa para conversar com o sistema no seu lugar.",
	},
	{
		id: "meta",
		label: "Meta",
		icon: Share2,
		descricao:
			"As credenciais do app do Facebook que ligam o Messenger, o Instagram e o WhatsApp oficial ao sistema.",
	},
	{
		id: "backup",
		label: "Backup",
		icon: DatabaseBackup,
		descricao:
			"Levar a configuração da empresa para um arquivo, e trazer de volta em outra instalação ou depois de uma reinstalação.",
	},
];

// Bloco de aviso: usado onde a configuração tem um efeito que costuma
// pegar as pessoas de surpresa.
const Aviso = ({ children }) => (
	<div className="flex gap-2 rounded-lg bg-muted p-3 text-xs leading-relaxed">
		<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
		<div>{children}</div>
	</div>
);

const SettingsCard = ({
	icon: Icon,
	title,
	description,
	action,
	footer,
	children,
}) => (
	<div className="overflow-hidden rounded-xl border bg-card">
		<div className="flex items-start justify-between gap-4 border-b px-5 py-4">
			<div className="flex min-w-0 items-start gap-3">
				{Icon ? (
					<span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
						<Icon className="h-4 w-4" />
					</span>
				) : null}
				<div className="min-w-0">
					<h2 className="text-sm font-semibold tracking-tight">{title}</h2>
					{description ? (
						<p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
					) : null}
				</div>
			</div>
			{action ? <div className="shrink-0">{action}</div> : null}
		</div>
		<div className="px-5 py-5">{children}</div>
		{footer ? (
			<div className="flex justify-end border-t bg-muted/20 px-5 py-3">{footer}</div>
		) : null}
	</div>
);

const Settings = () => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useWhatsApps();

	const isSuperAdmin = Boolean(
		user?.super || (user?.profile === "admin" && Number(user?.companyId) === 1)
	);

	const visibleSections = SECTIONS.filter(
		(section) => !section.superAdminOnly || isSuperAdmin
	);

	const [activeSection, setActiveSection] = useState(
		isSuperAdmin ? "geral" : "automacoes"
	);

	useEffect(() => {
		if (!isSuperAdmin && (activeSection === "geral" || activeSection === "aparencia")) {
			setActiveSection("automacoes");
		}
	}, [isSuperAdmin, activeSection]);

	const [settings, setSettings] = useState([]);

	// White-label
	const [whiteLabel, setWhiteLabel] = useState({
		appName: "",
		primaryColor: "#7367F0",
		logoUrl: null,
		backgroundUrl: null,
	});
	const [savingWhiteLabel, setSavingWhiteLabel] = useState(false);
	const logoInputRef = useRef(null);
	const backgroundInputRef = useRef(null);

	// Birthdays
	const [birthdaySettings, setBirthdaySettings] = useState({
		isEnabled: false,
		messageTemplate: "",
		sendHour: 9,
		whatsappId: null,
	});
	const [savingBirthday, setSavingBirthday] = useState(false);
	const [todayBirthdays, setTodayBirthdays] = useState([]);
	const [listaAniversarioAberta, setListaAniversarioAberta] = useState(false);

	// 2FA
	const [twoFactorEnabled, setTwoFactorEnabled] = useState(
		!!user?.twoFactorEnabled
	);
	const [twoFactorSetup, setTwoFactorSetup] = useState(null);
	const [twoFactorToken, setTwoFactorToken] = useState("");
	const [loading2FA, setLoading2FA] = useState(false);

	useEffect(() => {
		setTwoFactorEnabled(!!user?.twoFactorEnabled);
	}, [user]);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		if (!isSuperAdmin) return;
		(async () => {
			try {
				const { data } = await api.get("/settings/white-label");
				setWhiteLabel({
					appName: data.appName || "",
					primaryColor: data.primaryColor || "#7367F0",
					logoUrl: data.logoUrl || null,
					backgroundUrl: data.backgroundUrl || null,
				});
			} catch (err) {
				toastError(err);
			}
		})();
	}, [isSuperAdmin]);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/birthdays/settings");
				setBirthdaySettings({
					isEnabled: !!data.isEnabled,
					messageTemplate: data.messageTemplate || "",
					sendHour: data.sendHour ?? 9,
					whatsappId: data.whatsappId || null,
				});
			} catch (err) {
				toastError(err);
			}
		})();

		(async () => {
			try {
				const { data } = await api.get("/birthdays/today");
				setTodayBirthdays(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", (data) => {
			if (data.action === "update") {
				setSettings((prevState) => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex((s) => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async (key, value) => {
		try {
			await api.put(`/settings/${key}`, { value });
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const [generatingToken, setGeneratingToken] = useState(false);

	const getSettingValue = (key) => {
		const setting = settings.find((s) => s.key === key);
		return setting ? setting.value : "";
	};

	const handleCopyToken = () => {
		const token = getSettingValue("userApiToken");
		if (!token) {
			toast.error("Nenhuma chave para copiar. Gere uma chave primeiro.");
			return;
		}
		navigator.clipboard.writeText(token);
		toast.success(i18n.t("settings.success"));
	};

	const handleGenerateToken = async () => {
		setGeneratingToken(true);
		try {
			const newToken =
				typeof window !== "undefined" && window.crypto && window.crypto.randomUUID
					? (window.crypto.randomUUID() + window.crypto.randomUUID()).replace(/-/g, "")
					: Array.from({ length: 64 }, () =>
							Math.floor(Math.random() * 16).toString(16)
						).join("");

			await api.put("/settings/userApiToken", { value: newToken });
			toast.success("Nova chave da API gerada com sucesso!");
		} catch (err) {
			toastError(err);
		}
		setGeneratingToken(false);
	};

	// ----- White-label -----

	const handleSaveWhiteLabel = async () => {
		setSavingWhiteLabel(true);
		try {
			const { data } = await api.put("/settings/white-label", {
				appName: whiteLabel.appName,
				primaryColor: whiteLabel.primaryColor,
				logoUrl: whiteLabel.logoUrl,
				backgroundUrl: whiteLabel.backgroundUrl,
			});
			setWhiteLabel({
				appName: data.appName || "",
				primaryColor: data.primaryColor || "#7367F0",
				logoUrl: data.logoUrl || null,
				backgroundUrl: data.backgroundUrl || null,
			});
			toast.success("Aparência atualizada com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSavingWhiteLabel(false);
	};

	const handleUploadWhiteLabelFile = async (e, type) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const formData = new FormData();
		formData.append("file", file);
		try {
			const { data } = await api.post(
				`/settings/white-label/${type}`,
				formData
			);
			if (type === "logo") {
				setWhiteLabel((prev) => ({ ...prev, logoUrl: data.logoUrl }));
			} else {
				setWhiteLabel((prev) => ({
					...prev,
					backgroundUrl: data.backgroundUrl,
				}));
			}
			toast.success(
				type === "logo"
					? "Logo enviado com sucesso."
					: "Imagem de fundo enviada com sucesso."
			);
		} catch (err) {
			toastError(err);
		}
		e.target.value = "";
	};

	// ----- Birthdays -----

	const handleSaveBirthdaySettings = async () => {
		setSavingBirthday(true);
		try {
			await api.put("/birthdays/settings", {
				isEnabled: birthdaySettings.isEnabled,
				messageTemplate: birthdaySettings.messageTemplate,
				sendHour: +birthdaySettings.sendHour,
				whatsappId: birthdaySettings.whatsappId
					? +birthdaySettings.whatsappId
					: null,
			});
			toast.success("Configurações de aniversário salvas com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSavingBirthday(false);
	};

	// ----- 2FA -----

	const handleSetup2FA = async () => {
		setLoading2FA(true);
		try {
			const { data } = await api.get("/auth/2fa/setup");
			setTwoFactorSetup(data);
			setTwoFactorToken("");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	const handleEnable2FA = async () => {
		if (twoFactorToken.trim().length < 6) return;
		setLoading2FA(true);
		try {
			await api.post("/auth/2fa/enable", { token: twoFactorToken.trim() });
			setTwoFactorEnabled(true);
			setTwoFactorSetup(null);
			setTwoFactorToken("");
			if (user) {
				user.twoFactorEnabled = true;
			}
			toast.success("Autenticação em duas etapas ativada com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	const handleDisable2FA = async () => {
		setLoading2FA(true);
		try {
			await api.delete("/auth/2fa/disable");
			setTwoFactorEnabled(false);
			setTwoFactorSetup(null);
			if (user) {
				user.twoFactorEnabled = false;
			}
			toast.success("Autenticação em duas etapas desativada.");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	return (
		<div className="w-full px-4 py-6 sm:px-6 lg:px-8">
			{/* Cabeçalho */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold tracking-tight">
					{i18n.t("settings.title")}
				</h1>
				<p className="mt-1 text-sm text-muted-foreground">
					Ajustes que valem para a empresa inteira.
				</p>
			</div>

			<div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
				{/* Navegação lateral */}
				<nav className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
					{visibleSections.map((section) => {
						const Icon = section.icon;
						const active = activeSection === section.id;
						return (
							<button
								key={section.id}
								type="button"
								onClick={() => setActiveSection(section.id)}
								className={
									"flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors lg:w-full " +
									(active
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-accent hover:text-accent-foreground")
								}
							>
								<Icon className="h-4 w-4 shrink-0" />
								{section.label}
							</button>
						);
					})}
				</nav>

				{/* Conteúdo */}
				<div className="min-w-0 w-full space-y-4">
					{/* Uma linha por aba: para que serve o que está logo abaixo. */}
					<p className="text-sm leading-relaxed text-muted-foreground">
						{visibleSections.find((s) => s.id === activeSection)?.descricao}
					</p>

					{/* ---------------- Geral ---------------- */}
					{isSuperAdmin && activeSection === "geral" && (
						<SettingsCard
							icon={SlidersHorizontal}
							title="Quem pode criar conta"
							description="Vale para a tela de cadastro, aquela que aparece antes de entrar no sistema."
						>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div>
									<Label className="text-sm font-medium">
										{i18n.t("settings.settings.userCreation.name")}
									</Label>
									<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
										Liberado, qualquer pessoa que abrir o endereço do sistema
										consegue se cadastrar sozinha e entrar.
										<br />
										Bloqueado, só quem já está dentro cria gente nova, pela tela
										de Usuários.
									</p>
								</div>
								{settings.length > 0 && (
									<Select
										value={getSettingValue("userCreation")}
										onValueChange={(v) => handleChangeSetting("userCreation", v)}
									>
										<SelectTrigger className="w-full sm:w-48">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="enabled">
												{i18n.t("settings.settings.userCreation.options.enabled")}
											</SelectItem>
											<SelectItem value="disabled">
												{i18n.t("settings.settings.userCreation.options.disabled")}
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							</div>

							{getSettingValue("userCreation") === "enabled" && (
								<div className="mt-4">
									<Aviso>
										Do jeito que está agora, quem tiver o endereço do sistema
										entra sem você aprovar — inclusive alguém de fora da
										empresa. Se o seu time já está todo cadastrado, o mais
										seguro é bloquear.
									</Aviso>
								</div>
							)}
						</SettingsCard>
					)}

					{/* ---------------- Aparência ---------------- */}
					{isSuperAdmin && activeSection === "aparencia" && (
						<SettingsCard
							icon={Palette}
							title="Deixar o sistema com a cara da sua empresa"
							description="Troque o nome, a cor e o logo. Quem usa o sistema vê a sua marca, não a do fornecedor."
							footer={
								<Button onClick={handleSaveWhiteLabel} disabled={savingWhiteLabel}>
									{savingWhiteLabel && <Loader2 className="h-4 w-4 animate-spin" />}
									Aplicar para todo mundo
								</Button>
							}
						>
							<div className="space-y-5">
								<div className="space-y-1.5">
									<Label htmlFor="wl-app-name">Nome que aparece nas telas</Label>
									<Input
										id="wl-app-name"
										value={whiteLabel.appName}
										onChange={(e) =>
											setWhiteLabel((prev) => ({ ...prev, appName: e.target.value }))
										}
										placeholder="WhaTicket"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Aparece no canto superior esquerdo e na tela de entrar. Ex.:
										coloque “Atendimento Silva” e é isso que sua equipe lê todo
										dia. Em branco, fica o nome padrão.
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="wl-primary-color">Cor dos botões</Label>
									<p className="text-xs leading-relaxed text-muted-foreground">
										É a cor dos botões principais e dos destaques. Escolha uma
										cor da sua marca — o quadradinho colorido abre o seletor.
									</p>
									<div className="flex items-center gap-3">
										<input
											id="wl-primary-color"
											type="color"
											value={whiteLabel.primaryColor}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													primaryColor: e.target.value,
												}))
											}
											className="h-10 w-14 cursor-pointer rounded-md border bg-transparent p-1"
										/>
										<Input
											value={whiteLabel.primaryColor}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													primaryColor: e.target.value,
												}))
											}
											className="w-36 font-mono text-xs"
										/>
										<span
											className="hidden h-10 flex-1 rounded-md sm:block"
											style={{ backgroundColor: whiteLabel.primaryColor }}
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label>Logo da empresa</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Aparece no topo do menu. Use uma imagem com fundo
											transparente, se tiver.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() => logoInputRef.current && logoInputRef.current.click()}
											>
												<Upload className="h-4 w-4" />
												Enviar logo
											</Button>
											{/* Lista explícita em vez de "image/*": o backend
											    recusa .svg, que é XML e pode carregar script.
											    Sem isto o seletor ofereceria um arquivo que só
											    seria recusado depois do envio. */}
											<input
												id="wl-logo"
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={logoInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "logo")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.logoUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">{whiteLabel.logoUrl}</span>
													</>
												) : (
													"Nenhum logo enviado ainda"
												)}
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<Label>Imagem de fundo da tela de entrar</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											É o que se vê atrás do formulário de login. Sem imagem,
											fica o fundo padrão.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													backgroundInputRef.current && backgroundInputRef.current.click()
												}
											>
												<Upload className="h-4 w-4" />
												Enviar imagem
											</Button>
											<input
												id="wl-background"
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={backgroundInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "background")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.backgroundUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">
															{whiteLabel.backgroundUrl}
														</span>
													</>
												) : (
													"Nenhuma imagem enviada ainda"
												)}
											</p>
										</div>
									</div>
								</div>
							</div>
						</SettingsCard>
					)}

					{/* ---------------- Automações ---------------- */}
					{activeSection === "automacoes" && (
						<SettingsCard
							icon={Cake}
							title="Aniversáriantes"
							description="Ligado, o contato recebe uma mensagem de parabéns no dia do aniversário dele, sem ninguém precisar lembrar."
							action={
								<div className="flex items-center gap-2">
									<Label
										htmlFor="birthday-enabled"
										className="text-xs font-normal text-muted-foreground"
									>
										{birthdaySettings.isEnabled ? "Ligado" : "Desligado"}
									</Label>
									<Switch
										id="birthday-enabled"
										checked={birthdaySettings.isEnabled}
										onCheckedChange={(checked) =>
											setBirthdaySettings((prev) => ({
												...prev,
												isEnabled: checked,
											}))
										}
									/>
								</div>
							}
							footer={
								<Button
									onClick={handleSaveBirthdaySettings}
									disabled={savingBirthday}
								>
									{savingBirthday && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar os parabéns automáticos
								</Button>
							}
						>
							<div className="space-y-5">
								{/* Armadilha real: as configurações são gravadas, mas hoje
								    nada no servidor dispara o envio no horário marcado. */}
								<Aviso>
									<strong>O envio automático ainda não está funcionando.</strong>{" "}
									O que você configurar aqui fica guardado, mas nenhuma mensagem
									sai sozinha por enquanto. Por ora, os aniversariantes do dia
									aparecem na lista mais abaixo para você mandar o parabéns na
									mão.
								</Aviso>

								<div className="space-y-1.5">
									<Label htmlFor="birthday-template">
										O que o contato vai receber
									</Label>
									<Textarea
										id="birthday-template"
										rows={3}
										value={birthdaySettings.messageTemplate}
										onChange={(e) =>
											setBirthdaySettings((prev) => ({
												...prev,
												messageTemplate: e.target.value,
											}))
										}
										placeholder="Feliz aniversário, {{name}}! 🎉"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Escreva {"{{name}}"} onde o nome da pessoa deve entrar. Ex.:
										“Feliz aniversário, {"{{name}}"}!” chega no celular da Maria
										como “Feliz aniversário, Maria!”. Em branco, o sistema usa
										um parabéns simples.
									</p>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="birthday-hour">
											A que horas mandar
										</Label>
										<Input
											id="birthday-hour"
											type="number"
											min={0}
											max={23}
											value={birthdaySettings.sendHour}
											onChange={(e) =>
												setBirthdaySettings((prev) => ({
													...prev,
													sendHour: e.target.value,
												}))
											}
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Use a hora cheia, de 0 a 23. Ex.: 9 é nove da manhã, 14 é
											duas da tarde. Evite madrugada — a mensagem chega e
											acorda o cliente.
										</p>
									</div>
									<div className="space-y-1.5">
										<Label>Qual número vai mandar</Label>
										<Select
											value={
												birthdaySettings.whatsappId
													? String(birthdaySettings.whatsappId)
													: undefined
											}
											onValueChange={(v) =>
												setBirthdaySettings((prev) => ({
													...prev,
													whatsappId: +v,
												}))
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Escolha o número" />
											</SelectTrigger>
											<SelectContent>
												{whatsApps.map((w) => (
													<SelectItem key={w.id} value={String(w.id)}>
														{w.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs leading-relaxed text-muted-foreground">
											É o WhatsApp que aparece para o cliente como remetente.
											Sem escolher um número, nada é enviado.
										</p>
									</div>
								</div>

								{/* A lista fica recolhida, mas o resumo já responde
								    "tem alguém fazendo aniversário hoje?" sem abrir. */}
								<div className="rounded-lg border bg-muted/30">
									<button
										type="button"
										onClick={() => setListaAniversarioAberta((v) => !v)}
										className="flex w-full items-center gap-2 p-4 text-left"
									>
										{listaAniversarioAberta ? (
											<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
										) : (
											<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
										)}
										<span className="min-w-0 flex-1">
											<span className="block text-sm font-medium">
												Quem faz aniversário hoje
											</span>
											<span className="mt-0.5 block text-xs text-muted-foreground">
												{todayBirthdays.length === 0
													? "Ninguém hoje."
													: todayBirthdays.length === 1
														? "1 pessoa faz aniversário hoje."
														: `${todayBirthdays.length} pessoas fazem aniversário hoje.`}
											</span>
										</span>
										<Badge variant="secondary">{todayBirthdays.length}</Badge>
									</button>
									{listaAniversarioAberta && (
										<div className="border-t px-4 py-3">
											{todayBirthdays.length === 0 ? (
												<p className="text-xs leading-relaxed text-muted-foreground">
													Ninguém da sua lista de contatos faz aniversário
													hoje. Quem não tem a data de nascimento preenchida no
													cadastro nunca aparece aqui.
												</p>
											) : (
												<ul className="space-y-1 text-sm">
													{todayBirthdays.map((contact) => (
														<li
															key={contact.id}
															className="text-muted-foreground"
														>
															{contact.name}
															{contact.number ? ` — ${contact.number}` : ""}
														</li>
													))}
												</ul>
											)}
										</div>
									)}
								</div>
							</div>
						</SettingsCard>
					)}

					{/* ---------------- Segurança ---------------- */}
					{activeSection === "seguranca" && (
						<SettingsCard
							icon={ShieldCheck}
							title="Pedir um código extra ao entrar"
							description="Ligado, além da senha o sistema pede um número de 6 dígitos que só aparece no seu celular. Se roubarem sua senha, ainda não conseguem entrar."
							action={
								twoFactorEnabled ? (
									<Badge className="gap-1">
										<Check className="h-3 w-3" />
										Ativada
									</Badge>
								) : (
									<Badge variant="secondary">Desativada</Badge>
								)
							}
						>
							{twoFactorEnabled ? (
								<div className="space-y-4">
									<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
										<p className="text-sm leading-relaxed text-muted-foreground">
											Está ligado. Toda vez que você entrar, além da senha o
											sistema vai pedir o código do seu aplicativo.
										</p>
										<Button
											variant="outline"
											className="shrink-0 text-destructive hover:text-destructive"
											onClick={handleDisable2FA}
											disabled={loading2FA}
										>
											{loading2FA && <Loader2 className="h-4 w-4 animate-spin" />}
											Voltar a entrar só com a senha
										</Button>
									</div>
									<Aviso>
										Se você trocar de celular ou apagar o aplicativo sem
										desligar isto antes, você fica sem o código e não consegue
										mais entrar. Desligue aqui primeiro.
									</Aviso>
								</div>
							) : twoFactorSetup ? (
								<div className="space-y-4">
									<p className="text-sm leading-relaxed text-muted-foreground">
										No celular, instale um aplicativo de códigos (Google
										Authenticator ou Authy, ambos gratuitos), abra a câmera dele
										e aponte para o quadrado abaixo. O aplicativo passa a
										mostrar um número de 6 dígitos que troca a cada 30 segundos.
										Digite o número atual para confirmar.
									</p>
									<div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
										{twoFactorSetup.qrCodeDataUrl && (
											<img
												src={twoFactorSetup.qrCodeDataUrl}
												alt="QR Code 2FA"
												className="h-44 w-44 shrink-0 rounded-lg border bg-white p-2"
											/>
										)}
										<div className="w-full flex-1 space-y-3">
											<div className="space-y-1.5">
												<Label className="text-xs">
													Se a câmera não ler, digite este código no aplicativo
												</Label>
												<Input
													readOnly
													value={twoFactorSetup.secret || ""}
													className="font-mono text-xs"
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="twofa-token">
													Número que apareceu no aplicativo
												</Label>
												<Input
													id="twofa-token"
													inputMode="numeric"
													maxLength={6}
													placeholder="000000"
													value={twoFactorToken}
													onChange={(e) =>
														setTwoFactorToken(e.target.value.replace(/\D/g, ""))
													}
													className="font-mono tracking-widest"
												/>
											</div>
											<div className="flex gap-2">
												<Button
													variant="outline"
													onClick={() => setTwoFactorSetup(null)}
													disabled={loading2FA}
												>
													Cancelar
												</Button>
												<Button
													onClick={handleEnable2FA}
													disabled={loading2FA || twoFactorToken.length < 6}
												>
													{loading2FA && (
														<Loader2 className="h-4 w-4 animate-spin" />
													)}
													Conferir e ligar a proteção
												</Button>
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
									<p className="text-sm leading-relaxed text-muted-foreground">
										Hoje basta a sua senha para entrar. Você vai precisar de um
										celular com um aplicativo de códigos — leva uns dois
										minutos para configurar. Vale só para a sua conta, não
										muda nada para os outros usuários.
									</p>
									<Button
										className="shrink-0"
										onClick={handleSetup2FA}
										disabled={loading2FA}
									>
										{loading2FA && <Loader2 className="h-4 w-4 animate-spin" />}
										Começar a configurar
									</Button>
								</div>
							)}
						</SettingsCard>
					)}

					{/* ---------------- API ---------------- */}
					{activeSection === "api" && (
						<SettingsCard
							icon={KeyRound}
							title="Chave para ligar outro sistema a este"
							description="Serve para um programa de fora (seu site, uma loja, uma planilha) mandar mensagem por aqui sozinho. Se você não vai integrar nada, é só ignorar esta aba."
						>
							<div className="space-y-1.5">
								<Label htmlFor="api-token-setting">
									Sua chave — copie e entregue a quem for fazer a integração
								</Label>
								<div className="flex gap-2">
									<div className="relative flex-1">
										<Input
											id="api-token-setting"
											readOnly
											value={settings.length > 0 ? getSettingValue("userApiToken") : ""}
											placeholder="Nenhuma chave gerada. Clique em Gerar chave ->"
											className="font-mono text-xs pr-28"
										/>
										<Button
											type="button"
											size="sm"
											variant="secondary"
											onClick={handleGenerateToken}
											disabled={generatingToken}
											className="absolute right-1 top-1/2 -translate-y-1/2 h-7 gap-1.5 px-2.5 text-xs font-medium"
											title="Gerar nova chave da API"
										>
											{generatingToken ? (
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
											) : (
												<KeyRound className="h-3.5 w-3.5" />
											)}
											Gerar chave
										</Button>
									</div>
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={handleCopyToken}
										title="Copiar a chave"
									>
										<Copy className="h-4 w-4" />
									</Button>
								</div>
								<div className="pt-2">
									<Aviso>
										Trate esta chave como a senha do cofre: quem tiver ela
										consegue mandar mensagem de WhatsApp em nome da sua empresa,
										para qualquer cliente, sem precisar entrar no sistema. Não
										mande por grupo, e-mail aberto nem cole em conversa.
									</Aviso>
								</div>

								{/* A documentação mora numa tela só, com exemplos prontos e
								    um testador. Repetir o conteúdo aqui garantiria que uma
								    das duas cópias ficasse desatualizada. */}
								<div className="border-t pt-4">
									<p className="text-sm font-medium">
										Como usar em outro sistema
									</p>
									<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
										Endereço, campos, exemplos prontos de código e um testador
										que envia de verdade estão na tela da API.
									</p>
									<Button
										type="button"
										variant="outline"
										className="mt-3"
										onClick={() => navigate("/messages-api")}
									>
										<Plug className="h-4 w-4" />
										Abrir documentação da API
									</Button>
								</div>
							</div>
						</SettingsCard>
					)}

					{/* ---------------- Meta ---------------- */}
					{activeSection === "meta" && (
						<SettingsCard
							title="App da Meta"
							description="Preencha com os dados do app criado em developers.facebook.com. Sem eles, os botões de conectar Facebook, Instagram e WhatsApp oficial não aparecem na tela de Conexões."
						>
							<MetaSettings />
						</SettingsCard>
					)}

					{/* ---------------- Backup ---------------- */}
					{activeSection === "backup" && <BackupRestore />}
				</div>
			</div>
		</div>
	);
};

export default Settings;
